import { Controller } from "@hotwired/stimulus"

const SVG = "http://www.w3.org/2000/svg"
const SIZE = 900
const RADIUS = 270
const BAND = 14
const GAP = 0.012
const PALETTE = [ "#e11d48", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#0ea5e9", "#84cc16" ]

// Chord diagram of passports going from one event to their next one. Each directed flow is a
// ribbon from an arc on the event left to an arc on the event reached, coloured by where it left.
export default class extends Controller {
  static targets = [ "chart" ]
  static values = { events: Array, order: Array, flows: Array }

  connect() {
    this.layout()
    this.draw()
  }

  layout() {
    this.flows = this.flowsValue
    const totals = new Map()
    this.flows.forEach(([ from, to, count ]) => {
      totals.set(from, (totals.get(from) || 0) + count)
      totals.set(to, (totals.get(to) || 0) + count)
    })
    const ids = this.orderValue.filter((id) => totals.has(id))
    const total = [ ...totals.values() ].reduce((sum, value) => sum + value, 0)
    const perUnit = (Math.PI * 2 - GAP * ids.length) / total

    // Each event's arc holds its outgoing and incoming flows, ordered around the circle by the
    // other event so ribbons cross as little as possible.
    this.groups = new Map()
    let angle = 0
    ids.forEach((id, i) => {
      const group = { id, color: PALETTE[i % PALETTE.length], start: angle, end: angle + totals.get(id) * perUnit, slots: [] }
      angle = group.end + GAP
      this.groups.set(id, group)
    })
    this.flows.forEach((flow) => {
      this.groups.get(flow[0]).slots.push({ flow, other: flow[1], out: true })
      this.groups.get(flow[1]).slots.push({ flow, other: flow[0], out: false })
    })
    this.ribbons = new Map(this.flows.map((flow) => [ flow, {} ]))
    this.groups.forEach((group) => {
      let at = group.start
      const clockwise = (slot) => (ids.indexOf(slot.other) - ids.indexOf(group.id) + ids.length) % ids.length
      group.slots.sort((a, b) => clockwise(b) - clockwise(a) || a.out - b.out)
      group.slots.forEach(({ flow, out }) => {
        const span = [ at, at + flow[2] * perUnit ]
        this.ribbons.get(flow)[out ? "source" : "target"] = span
        at = span[1]
      })
    })
  }

  draw() {
    const svg = this.chartTarget
    svg.setAttribute("viewBox", `${-SIZE / 2} ${-SIZE / 2} ${SIZE} ${SIZE}`)
    svg.replaceChildren()
    const ribbons = element("g", { "fill-opacity": 0.55 })
    const arcs = element("g")
    svg.append(ribbons, arcs)

    this.ribbonElements = [ ...this.ribbons ].sort(([ a ], [ b ]) => b[2] - a[2]).map(([ flow, { source, target } ]) => {
      const path = element("path", { d: ribbon(source, target, RADIUS - 2), fill: this.groups.get(flow[0]).color, class: "transition-opacity duration-200" })
      path.append(element("title", {}, `${this.eventsValue[flow[0]]} → ${this.eventsValue[flow[1]]}: ${flow[2]}`))
      path.flow = flow
      ribbons.append(path)
      return path
    })

    this.groups.forEach((group) => {
      const g = element("g", { class: "cursor-pointer" })
      g.append(element("path", { d: band(group.start, group.end, RADIUS, RADIUS + BAND), fill: group.color }))
      const mid = (group.start + group.end) / 2
      const degrees = (mid * 180) / Math.PI - 90
      const flip = mid > Math.PI
      const name = this.eventsValue[group.id]
      g.append(element("text", {
        transform: `rotate(${degrees}) translate(${RADIUS + BAND + 8}) ${flip ? "rotate(180)" : ""}`,
        "text-anchor": flip ? "end" : "start", "dominant-baseline": "middle",
        class: "fill-stone-600 text-[13px] font-semibold"
      }, name.length > 24 ? `${name.slice(0, 23)}…` : name))
      g.addEventListener("pointerenter", () => this.highlight(group.id))
      g.addEventListener("click", () => this.highlight(this.selected === group.id ? null : group.id))
      arcs.append(g)
    })
    svg.addEventListener("pointerleave", (event) => { if (event.pointerType === "mouse") this.highlight(null) })
  }

  highlight(id) {
    this.selected = id
    this.ribbonElements.forEach((path) => {
      path.style.opacity = id == null || path.flow[0] === id || path.flow[1] === id ? 1 : 0.06
    })
  }
}

function element(name, attributes = {}, text = null) {
  const node = document.createElementNS(SVG, name)
  Object.entries(attributes).forEach(([ key, value ]) => node.setAttribute(key, value))
  if (text) node.textContent = text
  return node
}

function point(angle, radius) {
  return [ Math.sin(angle) * radius, -Math.cos(angle) * radius ].map((value) => value.toFixed(2)).join(",")
}

function arc(to, radius, sweep) {
  return `A${radius},${radius} 0 ${Math.abs(sweep) > Math.PI ? 1 : 0} ${sweep > 0 ? 1 : 0} ${point(to, radius)}`
}

function band(start, end, inner, outer) {
  return `M${point(start, outer)} ${arc(end, outer, end - start)} L${point(end, inner)} ${arc(start, inner, start - end)} Z`
}

function ribbon([ s0, s1 ], [ t0, t1 ], radius) {
  return `M${point(s0, radius)} ${arc(s1, radius, s1 - s0)} Q0,0 ${point(t0, radius)} ${arc(t1, radius, t1 - t0)} Q0,0 ${point(s0, radius)} Z`
}
